import React, { useState, useEffect } from 'react'
import AdminLayout from '../../components/admin/AdminLayout'
import { useAuth } from '../../context/AuthContext'

import { API_BASE, getImageUrl } from '../../utils/api'

interface City {
    _id: string; name: string; image: string; isActive: boolean; createdAt: string;
}

export default function AdminCities() {
    const { token } = useAuth()
    const [cities, setCities] = useState<City[]>([])
    const [loading, setLoading] = useState(true)
    const [formName, setFormName] = useState('')
    const [formImage, setFormImage] = useState('')
    const [editId, setEditId] = useState<string | null>(null)
    const [submitting, setSubmitting] = useState(false)
    const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null)
    const [uploading, setUploading] = useState(false)
    const [isModalOpen, setIsModalOpen] = useState(false)

    const fetchCities = () => {
        setLoading(true)
        fetch(`${API_BASE}/cities`)
            .then(r => r.json())
            .then(data => { if (Array.isArray(data)) setCities(data) })
            .catch(console.error)
            .finally(() => setLoading(false))
    }

    useEffect(() => { fetchCities() }, [])

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        setUploading(true)
        const fd = new FormData()
        fd.append('image', file)
        try {
            const res = await fetch(`${API_BASE}/upload/nearby-image`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd
            })
            const data = await res.json()
            if (data.url) setFormImage(data.url)
        } catch (err) { console.error(err) }
        finally { setUploading(false) }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!formName.trim()) { setMsg({ type: 'error', text: 'Regional designation is required' }); return }
        setSubmitting(true); setMsg(null)
        try {
            const url = editId ? `${API_BASE}/cities/${editId}` : `${API_BASE}/cities`
            const method = editId ? 'PUT' : 'POST'
            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ name: formName.trim(), image: formImage })
            })
            const data = await res.json()
            if (res.ok) {
                setFormName(''); setFormImage(''); setEditId(null)
                setIsModalOpen(false)
                setMsg({ type: 'success', text: editId ? 'Regional record synchronized.' : 'New regional hub established.' })
                fetchCities()
                setTimeout(() => setMsg(null), 3000)
            } else {
                setMsg({ type: 'error', text: data.message || 'Operation failed' })
            }
        } catch { setMsg({ type: 'error', text: 'Terminal connectivity error' }) }
        finally { setSubmitting(false) }
    }

    const handleEdit = (city: City) => {
        setEditId(city._id); setFormName(city.name); setFormImage(city.image)
        setMsg(null)
        setIsModalOpen(true)
    }

    const handleDelete = async (id: string) => {
        if (!window.confirm('Decommission this regional hub?')) return
        try {
            await fetch(`${API_BASE}/cities/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            })
            fetchCities()
        } catch { }
    }

    const handleToggleActive = async (city: City) => {
        try {
            await fetch(`${API_BASE}/cities/${city._id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ isActive: !city.isActive })
            })
            fetchCities()
        } catch { }
    }

    return (
        <AdminLayout>
            <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl md:text-4xl font-bold text-black mb-2 leading-none">Regional Hubs Matrix</h1>
                    <p className="text-[14px] md:text-[15px] font-bold text-black leading-relaxed">Manage geographic sectors and metropolitan nodes globally across the platform protocol.</p>
                </div>
                <button 
                    onClick={() => {
                        setEditId(null)
                        setFormName('')
                        setFormImage('')
                        setIsModalOpen(true)
                    }}
                    className="flex items-center gap-2 px-8 py-4 bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50 rounded-md font-bold transition-all shadow-xl active:scale-95"
                >
                    <i className="fa-solid fa-plus-circle"></i>
                    <span>Initialize Hub Protocol</span>
                </button>
            </div>

            {msg && (
                <div className={`mb-6 p-4 rounded-md flex items-center gap-3 animate-in fade-in slide-in-from-top-4 duration-300 ${
                    msg.type === 'success' 
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' 
                        : 'bg-rose-50 text-rose-700 border border-rose-100'
                }`}>
                    <i className={`fa-solid ${msg.type === 'success' ? 'fa-circle-check' : 'fa-circle-exclamation'}`}></i>
                    <p className="text-sm font-bold">{msg.text}</p>
                </div>
            )}

            <div className="bg-white dark:bg-slate-900 rounded-md border border-blue-900/10 dark:border-slate-800 shadow-sm overflow-hidden min-h-[500px]">
                <div className="p-8 border-b border-blue-900/10 dark:border-slate-800 flex items-center justify-between bg-white/50">
                    <div>
                         <h1 className="text-3xl font-bold text-[#05144b] mb-2 leading-none">City Portfolio</h1>
                    <p className="text-[#05144b] font-semibold text-[14px]">Manage active urban nodes and locality mapping.</p>
                    </div>
                </div>

                {loading ? (
                    <div className="p-12 text-center text-black">
                        <div className="animate-spin w-8 h-8 border-4 border-[#05144b] border-t-transparent rounded-full mx-auto mb-4"></div>
                        <p className="text-sm font-bold text-black">Synchronizing Map Nodes...</p>
                    </div>
                ) : cities.length === 0 ? (
                    <div className="p-12 text-center">
                        <div className="w-16 h-16 bg-white dark:bg-slate-800/50 rounded-md flex items-center justify-center mx-auto mb-4">
                            <i className="fa-solid fa-city text-black/10 text-2xl"></i>
                        </div>
                        <p className="text-black font-bold">No hubs established</p>
                        <p className="text-[12px] text-black/40 mt-1 font-bold">Initiate protocol to localize first node</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr>
                                    <th className="px-10 py-5 text-left text-[12px] md:text-[13px] font-bold text-black border-b border-gray-100">Regional Node</th>
                                    <th className="px-10 py-5 text-left text-[12px] md:text-[13px] font-bold text-black border-b border-gray-100">Operational State</th>
                                    <th className="px-10 py-5 text-left text-[12px] md:text-[13px] font-bold text-black border-b border-gray-100">Established</th>
                                    <th className="px-10 py-5 text-right text-[12px] md:text-[13px] font-bold text-black border-b border-gray-100">Executive Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {cities.map(city => (
                                    <tr key={city._id} className="group hover:bg-white/50 dark:hover:bg-slate-800/20 transition-all">
                                        <td className="px-10 py-2">
                                            <div className="flex items-center gap-5">
                                                <div className="w-11 h-11 rounded-md overflow-hidden border border-slate-50 shadow-sm group-hover:shadow-md transition-all">
                                                    {city.image ? (
                                                        <img src={getImageUrl(city.image)} alt={city.name} className="w-full h-full object-cover" />
                                                    ) : (
                                                        <div className="w-full h-full bg-white flex items-center justify-center text-blue-100">
                                                            <i className="fa-solid fa-city text-xs"></i>
                                                        </div>
                                                    )}
                                                </div>
                                                <div>
                                                    <div className="text-[15px] md:text-[16px] font-bold text-black">{city.name}</div>
                                                    <div className="text-[10px] font-bold text-black/40 mt-0.5 tracking-widest">NODE_{city._id.substring(0, 6)}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-10 py-2">
                                            <button 
                                                onClick={() => handleToggleActive(city)}
                                                className={`px-4 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-widest transition-all border ${
                                                    city.isActive 
                                                    ? 'bg-emerald-50 text-emerald-600 border-emerald-100' 
                                                    : 'bg-white text-black border-gray-100'
                                                }`}
                                            >
                                                {city.isActive ? 'Active Node' : 'Suspended'}
                                            </button>
                                        </td>
                                        <td className="px-10 py-2">
                                            <span className="text-[12px] font-bold text-black/40">
                                                {new Date(city.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                            </span>
                                        </td>
                                        <td className="px-10 py-2 text-right">
                                            <div className="flex justify-end gap-2">
                                                <button 
                                                    className="w-9 h-9 rounded-md bg-white border border-[#05144b] text-[#05144b] hover:bg-blue-50 transition-all flex items-center justify-center shadow-lg active:scale-95"
                                                    onClick={() => handleEdit(city)}
                                                >
                                                    <i className="fa-solid fa-pen-nib text-xs"></i>
                                                </button>
                                                <button 
                                                    className="w-9 h-9 rounded-md bg-white border border-blue-900/10 text-rose-600 hover:bg-rose-600 hover:text-white transition-all flex items-center justify-center shadow-sm active:scale-95"
                                                    onClick={() => handleDelete(city._id)}
                                                >
                                                    <i className="fa-solid fa-trash-can text-xs"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal Dialog */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#05144b]/40 backdrop-blur-sm animate-in fade-in duration-300">
                    <div className="bg-white dark:bg-slate-900 w-full max-w-2xl rounded-md border border-gray-100 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
                        <div className="p-8 border-b border-gray-100 flex items-center justify-between bg-white">
                            <div className="flex items-center gap-5">
                                <div className="w-12 h-12 bg-white border border-[#05144b] text-[#05144b] rounded-md flex items-center justify-center shadow-xl">
                                    <i className="fa-solid fa-map text-lg"></i>
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold text-black tracking-tight capitalize">
                                        {editId ? 'Modify Regional Hub' : 'Establish New Hub'}
                                    </h2>
                                    <p className="text-[13px] font-bold text-black/40 mt-1 leading-none">Regional localization protocol</p>
                                </div>
                            </div>
                            <button 
                                onClick={() => setIsModalOpen(false)}
                                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-all text-black"
                            >
                                <i className="fa-solid fa-xmark text-lg"></i>
                            </button>
                        </div>
                        <div className="p-8">
                            <form onSubmit={handleSubmit} className="space-y-8">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    <div>
                                        <label className="block text-[13px] font-bold text-black uppercase mb-3">Regional Designation</label>
                                         <input 
                                            type="text" 
                                            className="w-full px-6 py-4 rounded-md bg-white border border-gray-100 focus:ring-2 focus:ring-[#05144b] focus:border-[#05144b] text-black transition-all placeholder:text-gray-300 font-bold outline-none shadow-sm"
                                            value={formName} 
                                            onChange={e => setFormName(e.target.value)} 
                                            placeholder="Metropolitan name..." 
                                            required 
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-[13px] font-bold text-black uppercase mb-3">Visual Identification</label>
                                        <div className="relative group">
                                             <div className={`
                                                relative h-14 rounded-md border-2 border-dashed transition-all flex items-center justify-between px-6 overflow-hidden
                                                ${formImage ? 'border-[#05144b] bg-blue-50/30' : 'border-gray-100 hover:border-[#05144b]/30 bg-white'}
                                            `}>
                                                {formImage ? (
                                                    <div className="flex items-center gap-4">
                                                        <img 
                                                            src={getImageUrl(formImage)} 
                                                            alt="Preview" 
                                                            className="h-8 w-8 object-cover rounded shadow-sm border border-slate-50"
                                                        />
                                                        <p className="text-[11px] font-bold text-black">Hub Visual Buffered</p>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-3">
                                                        <i className="fa-solid fa-cloud-arrow-up text-black/30"></i>
                                                        <p className="text-[11px] font-bold text-black/60">{uploading ? 'Transmitting...' : 'Upload Regional Map'}</p>
                                                    </div>
                                                )}
                                                <input 
                                                    type="file" 
                                                    className="absolute inset-0 opacity-0 cursor-pointer z-20"
                                                    onChange={handleImageUpload} 
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex gap-4 pt-4">
                                    <button 
                                        type="button"
                                        onClick={() => setIsModalOpen(false)}
                                        className="flex-1 py-4 text-[15px] font-bold text-black/30 hover:text-black transition-colors"
                                    >
                                        Abort
                                    </button>
                                         <button 
                                        className={`
                                            flex-[2] py-4 rounded-md font-bold shadow-2xl transition-all flex items-center justify-center gap-2 active:scale-95
                                            ${submitting 
                                                ? 'bg-[#05144b]/50 text-white cursor-not-allowed' 
                                                : 'bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50'}
                                        `}
                                        type="submit" 
                                        disabled={submitting}
                                    >
                                        {submitting ? 'Processing...' : editId ? 'Synchronize Record' : 'Establish Node'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    )
}


